import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, scrollToId } from "@/constants/site";
import { useSiteImage } from "@/context/ImagesContext";

// LOGO SPOT: if you upload a logo via /manage-images it replaces this wordmark automatically.
const LogoMark = () => (
  <svg width="30" height="30" viewBox="0 0 64 64" aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="#1B3B2B" />
    <text x="32" y="44" fontFamily="Georgia, serif" fontSize="34" fill="#C8A261" textAnchor="middle">
      M
    </text>
  </svg>
);

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const logoSrc = useSiteImage("logo", null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    scrollToId(target);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/90 backdrop-blur-md border-b border-coffee-soft py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3"
          aria-label="Masthare Estate — back to top"
        >
          {logoSrc ? (
            <img
              data-testid="nav-logo-image"
              src={logoSrc}
              alt="Masthare Estate logo"
              className="h-11 sm:h-12 w-auto object-contain"
            />
          ) : (
            <>
              <LogoMark />
              <span className="text-left">
                <span
                  className={`font-display text-xl sm:text-2xl leading-none block transition-colors duration-500 ${
                    scrolled ? "text-espresso" : "text-cream"
                  }`}
                >
                  Masthare Estate
                </span>
                <span
                  className={`font-mono-alt text-[9px] tracking-[0.3em] uppercase transition-colors duration-500 ${
                    scrolled ? "text-amber-brand" : "text-gold"
                  }`}
                >
                  Est. 1844 · Sakleshpur
                </span>
              </span>
            </>
          )}
        </button>

        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <button
              key={link.target}
              data-testid={`nav-link-${link.target}`}
              onClick={() => go(link.target)}
              className={`text-sm tracking-[0.15em] uppercase font-medium transition-colors duration-300 hover:text-gold ${
                scrolled ? "text-coffee" : "text-cream/85"
              }`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <button
          data-testid="nav-menu-button"
          className={`md:hidden p-2 transition-colors ${scrolled ? "text-espresso" : "text-cream"}`}
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden absolute inset-x-0 top-full bg-espresso/97 backdrop-blur-lg border-t border-cream/10"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV_LINKS.map((link, i) => (
                <motion.button
                  key={link.target}
                  data-testid={`nav-mobile-link-${link.target}`}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.35 }}
                  onClick={() => go(link.target)}
                  className="text-left font-display text-2xl text-cream hover:text-gold transition-colors"
                >
                  {link.label}
                </motion.button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
